// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import React, { useEffect, useState } from "react"
import { useLocales } from "react-localized"
import {
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonList,
} from "@ionic/react"
import {
  AddressComponentValidator,
  CompanyNameValidator,
  IUser,
  IonItemInput,
  LoadingIonButton,
  PhonenumberValidator
} from "../../../lib"
import { VATCountrySelector } from "./VATCountrySelector"


interface CreateAccountComponentProps {
  user: IUser
  onSubmit(data: any): Promise<void>
}


const CreateAccountComponent: React.FC<CreateAccountComponentProps> = ({user, onSubmit}) => {
  const state: {[key: string]: string} = (!sessionStorage.getItem("CreateAccountComponent.state"))
    ? {}
    : JSON.parse(sessionStorage.getItem("CreateAccountComponent.state") as string)
  const {gettext} = useLocales()
  const addressErrorMessages = {
    capitalize: gettext("An address line has at least one uppercase character."),
    invalidCharacters: gettext("The address line contains invalid characters."),
    invalidEnd: gettext("An address line can not end with this character."),
    invalidStart: gettext("An address line can not start with this character."),
    minLength: gettext("An address line has at least two characters"),
    maxLength: gettext("This address line is too long."),
    multipleSpaces: gettext("Can not contain more than one consecutive spaces."),
    required: gettext("At least one address line is required."),
  }
  const [address1, setAddress1] = useState<[boolean, string]>([false, state.address1 || ""])
  const [address2, setAddress2] = useState<[boolean, string]>([false, state.address2 || ""])
  const [city, setCity] = useState<[boolean, string]>([false, state.city || ""])
  const [country, setCountry] = useState<string|null>(state.country || null)
  const [errors, setErrors] = useState<{[key: string]: string[] | undefined}>({})
  const [isValid, setIsValid] = useState<boolean>(false)
  const [organizationName, setOrganizationName] = useState<[boolean, string]>([false, state.organizationName || ""])
  const [phonenumber, setPhonenumber] = useState<[boolean, string]>([false, state.phonenumber || ""])
  const [postalCode, setPostalCode] = useState<[boolean, string]>([false, state.postalCode || ""])
  const [vat, setVat] = useState<string|null>(state.vat || null)
  const successMessage = (!!isValid)
    ? gettext("Click or tap the Create my Account button to proceed.")
    : gettext("Please fill out the remaining fields.")

  // Validation
  useEffect(() => {
    setIsValid(
      !!organizationName[0]
      && !!address1[0]
      && !!city[0]
      && !!postalCode[0]
      && !!country
      && !!phonenumber[0]
    )
  }, [address1, address2, city, country, organizationName, phonenumber, postalCode, vat])

  // Handle error response from server
  useEffect(() => {
    // TODO: inconsistent response from server
    if (!!errors.vat_number || !!errors.vatNumber) {
      console.log("Invalid VAT")
    }
    setVat((!!errors.vat_number || !!errors.vatNumber) ? null : vat)
  }, [errors])

  return (
    <>
      <IonList lines="none">
        <IonItemGroup>
          <IonItemDivider>
            <IonLabel>{gettext("Contact information")}</IonLabel>
          </IonItemDivider>
          <IonItemInput
            enterkeyHint="next"
            helpText={gettext("Click or tap to enter your phone number")}
            inputmode="tel"
            label={gettext("Phone number")}
            lines="inset"
            onReturn={(value) => console.log(value)}
            onValidated={(isValid, value) => setPhonenumber([isValid, value])}
            successMessage={successMessage}
            transformers={[
              (v: string) => v.replace(/[^\+0-9]/, '')
            ]}
            validator={new PhonenumberValidator({
              required: true,
              errorMessages: {
                invalid: gettext("Not a valid phone number."),
                required: gettext("Enter a phone number to proceed."),
                mustStartWithPlus: gettext("Use the international phone number format.")
              }
            })}
            value={phonenumber[1]}
          />
        </IonItemGroup>
        <IonItemGroup>
          <IonItemDivider>
            <IonLabel>{gettext("Billing details")}</IonLabel>
          </IonItemDivider>
          <IonItemInput
            autoCapitalize="sentences"
            disabled={!phonenumber[0]}
            enterkeyHint="next"
            helpText={gettext("Click or tap to enter your company name")}
            label={gettext("Organization name")}
            lines="inset"
            onValidated={(isValid, value) => {
              setOrganizationName([isValid, value])
            }}
            successMessage={successMessage}
            validator={new CompanyNameValidator({
              errorMessages: {
                capitalize: gettext("A company name has at least one uppercase character."),
                invalidCharacters: gettext("The company name contains invalid characters."),
                invalidEnd: gettext("A company name can not end with this character."),
                invalidStart: gettext("A company name can not start with this character."),
                minLength: gettext("A company name has at least two characters"),
                maxLength: gettext("This company name is too long."),
                multipleSpaces: gettext("Can not contain more than one consecutive spaces."),
                required: gettext("Enter the company name to proceed."),
              }
            })}
            value={organizationName[1]}
          />
          <VATCountrySelector
            country={country}
            setCountry={setCountry}
            setVatNumber={setVat}
            vatCountry={null}
            vatNumber={vat}
          />
          <IonItemDivider>
            <IonLabel>{gettext("Business address")}</IonLabel>
          </IonItemDivider>
          <IonItemInput
            autoCapitalize="sentences"
            enterkeyHint="next"
            disabled={!country || !phonenumber[0]}
            helpText={gettext("Click or tap to enter your address")}
            label={gettext("Address line 1")}
            lines="inset"
            onValidated={(isValid, value) => {
              setAddress1([isValid, value])
            }}
            successMessage={successMessage}
            validator={new AddressComponentValidator({
              required: true,
              errorMessages: addressErrorMessages
            })}
            value={address1[1]}
          />
          <IonItemInput
            autoCapitalize="sentences"
            enterkeyHint="next"
            disabled={!country || !phonenumber[0]}
            helpText={gettext("Optional")}
            label={gettext("Address line 2")}
            lines="inset"
            onValidated={(isValid, value) => {
              setAddress2([isValid, value])
            }}
            successMessage={successMessage}
            validator={new AddressComponentValidator({
              required: false,
              errorMessages: addressErrorMessages
            })}
            value={address2[1]}
          />
          <IonItemInput
            autoCapitalize="sentences"
            disabled={!country || !phonenumber[0]}
            enterkeyHint="next"
            helpText={gettext("Click or tap to enter your city")}
            label={gettext("City")}
            lines="inset"
            onValidated={(isValid, value) => setCity([isValid, value])}
            successMessage={successMessage}
            value={city[1]}
          />
          <IonItemInput
            disabled={!country || !phonenumber[0]}
            enterkeyHint="next"
            helpText={gettext("Click or tap to enter your postal code")}
            label={gettext("Postal code")}
            lines="inset"
            onValidated={(isValid, value) => setPostalCode([isValid, value])}
            successMessage={successMessage}
            transformers={[
              (v: string) => (country === "NL") ? v.toUpperCase() : v,
              (v: string) => (country === "NL") ? v.replace(/\s+/, '') : v,
            ]}
            value={postalCode[1]}
          />
        </IonItemGroup>
        <IonItem style={{marginTop: "22px"}}>
          <LoadingIonButton
            disabled={!isValid}
            onError={(error) => {
              setErrors(error.fieldErrors || {})
            }}
            onSubmit={async () => {
              sessionStorage.setItem("CreateAccountComponent.state", JSON.stringify({
                phonenumber: phonenumber[1],
                organizationName: organizationName[1],
                vat: vat,
                address1: address1[1],
                address2: address2[1],
                city: city[1],
                postalCode: postalCode[1],
                country: country
              }))
              await onSubmit({
                organizationName: organizationName[1],
                organizationContact: {
                  firstName: user.getGivenName(),
                  lastName: user.getFamilyName(),
                  email: user.getEmail(),
                  phonenumber: phonenumber[1]
                },
                businessAddress: {
                  address1: address1[1],
                  address2: address2[1],
                  postalCode: postalCode[1],
                  city: city[1],
                  country: country
                },
                vatNumber: vat
              })
            }}
            style={{margin: "0 auto", minWidth: "256px"}}
          >
            {gettext("Create account")}
          </LoadingIonButton>
        </IonItem>
      </IonList>
    </>
  )
}


export { CreateAccountComponent }